import { forwardRef, useLayoutEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  InputAdornment,
  Popover,
  TextField,
  TextareaAutosize,
  styled,
  useTheme,
} from '@mui/material';

import AppButton from '@/shared/ui/AppButton';
import ScrollableContentContainer from '@/shared/ui/ScrollableContentContainer';

export const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.15)',
  },
}));

export const PopoverContent = styled(Box)(() => ({
  width: '32rem',
  padding: '12px',
  display: 'grid',
  gridTemplateRows: 'minmax(0, 1fr) auto',
  gap: '12px',
  height: '32.5rem',
  overflow: 'hidden',
}));

export const CommentsList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
}));

export const EmptyStateText = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.grey[800],
  textAlign: 'center',
  padding: '1rem 0',
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '160%',
  fontFamily: '"Neue Frutiger World", sans-serif',
}));

export const LoadingContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100px',
}));

export const ComposerArea = styled('div')(() => ({
  flexShrink: 0,
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '2.625rem',
    borderRadius: '0.75rem',
    padding: '0.25rem',
    backgroundColor: theme.palette.accent.light,
    boxShadow: '0 2px 4px 0 rgba(127, 82, 213, 0.20)',
    display: 'flex',
    alignItems: 'center',
    '& fieldset': {
      borderColor: theme.palette.accent.main,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.accent.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.accent.main,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.accent.light,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: theme.palette.grey[900],
    fontSize: '0.75rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '160%',
    fontFamily: '"Neue Frutiger World", sans-serif',
    textAlign: 'left',
    paddingLeft: '0.5rem',
    paddingRight: '2rem',
    '&::placeholder': {
      color: theme.palette.grey[800],
      textAlign: 'left',
      fontSize: '0.75rem',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '160%',
      opacity: 1,
    },
  },
}));

export const ButtonsContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '4px',
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.accent.main,
  color: theme.palette.accent.contrastText,
  padding: '4px 15px',
  fontSize: '12px',
  borderRadius: '26px',
  textTransform: 'none',
  fontWeight: 500,
  fontFamily: '"Neue Frutiger World", sans-serif',
  '&:hover': {
    backgroundColor: theme.palette.accent.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

export const SendIconButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => (
  <AppButton
    variant="p-rounded"
    size="small"
    onClick={onClick}
    disabled={disabled}
    sx={{ padding: '0.25rem', minWidth: 0, marginRight: '0.25rem' }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      <path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" />
      <path d="M6 12h16" />
    </svg>
  </AppButton>
);

export const CommentInputAdornment = styled(InputAdornment)(() => ({
  position: 'absolute',
  right: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  marginRight: 0,
}));

export const ScrollableTextareaInput = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ style, value, ...props }, ref) => {
  const theme = useTheme();
  const [contentKey, setContentKey] = useState(0);
  const prevHeightRef = useRef(0);
  const isMountedRef = useRef(false);

  useLayoutEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    if (!value) {
      setContentKey((k) => k + 1);
      prevHeightRef.current = 0;
      return;
    }

    if (ref && 'current' in ref && ref.current) {
      const currentHeight = ref.current.scrollHeight;

      if (currentHeight !== prevHeightRef.current) {
        prevHeightRef.current = currentHeight;
        setContentKey((k) => k + 1);
      }
    }
  }, [value, ref]);

  return (
    <ScrollableContentContainer
      key={contentKey}
      maxHeight="4.2rem"
      sx={{ flex: 1, marginRight: '2rem' }}
      innerSx={{ height: 'auto', padding: '0 !important' }}
      smoothScroll
    >
      <TextareaAutosize
        ref={ref}
        value={value}
        {...props}
        style={{
          ...style,
          width: '100%',
          boxSizing: 'border-box',
          resize: 'none',
          border: 'none',
          outline: 'none',
          padding: '0 0.5rem 0 0.5rem',
          marginRight: '0.25rem',
          font: 'inherit',
          color: theme.palette.grey[900],
          backgroundColor: 'transparent',
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: '160%',
          fontFamily: '"Neue Frutiger World", sans-serif',
          textAlign: 'left',
        }}
      />
    </ScrollableContentContainer>
  );
});

ScrollableTextareaInput.displayName = 'ScrollableTextareaInput';
